import { API_URLS } from "@/config/urls";
import { handleAxiosError, isAxiosError } from "@/lib/apiErrors";
import { api } from "@/lib/axios";
import type {
  InvitableUser,
  PaginatedResponse,
  StudyGroupMembershipResponse,
  StudyGroupYourMembershipResponse,
  User,
} from "@/lib/humhub-api/types";

export const studyGroupMembershipApi = {
  getStudyGroupMemberships: async (
    id: string | number,
    perPage = 20,
    page = 1,
    keyword?: string,
    membershipStatus?: number,
  ): Promise<PaginatedResponse<StudyGroupMembershipResponse>> => {
    try {
      const response = await api.get<
        PaginatedResponse<StudyGroupMembershipResponse>
      >(`${API_URLS.STUDY_GROUP}/${id}/membership/find-by-membership-status`, {
        params: {
          limit: perPage,
          page,
          keyword,
          membershipStatus,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching study group memberships",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getStudyGroupYourMembership: async (
    studyGroupId: string | number,
  ): Promise<StudyGroupYourMembershipResponse> => {
    try {
      const response = await api.get<StudyGroupYourMembershipResponse>(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/current-user`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching your study group membership",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getAdmins: async (
    studyGroupId: string | number,
    perPage = 20,
    page = 1,
  ): Promise<PaginatedResponse<StudyGroupMembershipResponse>> => {
    try {
      const response = await api.get<
        PaginatedResponse<StudyGroupMembershipResponse>
      >(`${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/admins`, {
        params: {
          limit: perPage,
          page,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching study group admins",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getInvitableUsers: async (
    studyGroupId: string | number,
    keyword?: string,
  ): Promise<PaginatedResponse<InvitableUser>> => {
    try {
      const response = await api.get(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/invitable-users`,
        {
          params: keyword ? { keyword } : {},
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching invitable users",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getMentionAvailableUsers: async (
    studyGroupId: string | number,
    keyword?: string,
  ): Promise<User[]> => {
    try {
      const response = await api.get(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/filter-mention-available-users`,
        {
          params: keyword ? { keyword } : {},
        },
      );
      interface MentionUserItem {
        user: User;
      }
      return (
        response.data.results?.map((item: MentionUserItem) => item.user) || []
      );
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching mention available users",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  updateMemberRole: async (
    studyGroupId: string | number,
    userId: string | number,
    role: "admin" | "moderator" | "member",
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.put(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/${userId}/role`,
        { role },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while updating the member role",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
};
